import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectEditStyleModalOpen, selectEditStyleModalData } from '@/store/globalStyles/selectors';
import { closeEditStyleModal } from '@/store/globalStyles/slice';
import { applyGlobalStyle, createGlobalStyle } from '@/store/globalStyles/thunks';
import { selectCurrentPageId } from '@/store/pages/selectors';
import { showToast } from '@/store/notifications/slice';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { ASSETS_PANEL } from '@builder4/shared/utils/constants';
import type { GlobalStyle } from '@/store/globalStyles/types';

interface EditGlobalStyleModalProps {
  onStylesClick?: (elementName: string) => void;
  onStylesEditClick?: (
    style: GlobalStyle,
    mode?: 'edit' | 'create',
    isBrandProfileStyle?: boolean,
  ) => void;
  isBrandProfileStyle?: boolean;
}

export const EditGlobalStyleModal: React.FC<EditGlobalStyleModalProps> = ({
  onStylesClick,
  onStylesEditClick,
  isBrandProfileStyle,
}) => {
  const dispatch = useAppDispatch();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isOpen = useAppSelector(selectEditStyleModalOpen);
  const styleData = useAppSelector(selectEditStyleModalData);
  const currentPageId = useAppSelector(selectCurrentPageId);

  const handleClose = () => {
    dispatch(closeEditStyleModal());
  };

  // Handle dialog open/close
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && styleData) {
      dialog.showModal();
    } else {
      dialog.close();
    }
  }, [isOpen, styleData]);

  // Let dialog handle ESC automatically, just sync with Redux state
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [dispatch]);

  const handleEditStyle = () => {
    if (!styleData) return;
    dispatch(closeEditStyleModal());
    onStylesClick?.(ASSETS_PANEL.STYLES_EDIT);
    onStylesEditClick?.(styleData, undefined, isBrandProfileStyle);
  };

  const handleCreateNewStyle = async () => {
    if (!styleData) return;
    dispatch(closeEditStyleModal());

    const payload = {
      label: `${styleData.label} Copy`,
      global_style: styleData.globalStyle,
      showSuccessToast: false,
    };

    try {
      const newStyle = await dispatch(createGlobalStyle(payload)).unwrap();

      if (currentPageId && newStyle?.id) {
        const appliedStyle = await dispatch(
          applyGlobalStyle({
            pageId: currentPageId,
            globalStyleId: newStyle.id,
            showSuccessToast: false,
          }),
        ).unwrap();

        if (appliedStyle) {
          dispatch(
            showToast({
              text: 'Global style duplicated and applied successfully!',
              state: 'success',
            }),
          );
        }
      }

      if (newStyle) {
        onStylesClick?.(ASSETS_PANEL.STYLES_EDIT);
        onStylesEditClick?.(newStyle);
      }
    } catch (error) {
      console.error('Failed to create new style:', error);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="fixed top-1/2 left-1/2 w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      {/* Close button */}
      <div className="absolute top-4 right-4">
        <IconSimpleButton size={24} icon={IconClose} onClick={handleClose} />
      </div>

      {/* Title */}
      <h2 className="mb-3 text-lg font-semibold text-neutral-900">
        {isBrandProfileStyle ? 'Edit Brand Kit?' : 'Edit Global Style?'}
      </h2>

      {/* Body */}
      <p className="mb-6 text-sm text-neutral-600">
        This is a {isBrandProfileStyle ? 'brand kit' : 'global style'}. Changes will update all
        pages this style appears. Published pages won&apos;t change until you republish them.
      </p>

      {/* Buttons */}
      <div className="flex flex-col gap-2">
        <PrimaryButton
          text={isBrandProfileStyle ? 'Edit Brand Kit' : 'Edit Style'}
          size="md"
          state="default"
          icon={null}
          className="w-full"
          onClick={handleEditStyle}
        />
        {!isBrandProfileStyle && (
          <SecondaryButton
            text="Create New Style"
            size="md"
            state="default"
            icon={null}
            className="w-full"
            onClick={handleCreateNewStyle}
          />
        )}
      </div>
    </dialog>
  );
};

export default EditGlobalStyleModal;
